import { MarkNode } from "@lexical/mark";
import type { MarkNode as LexicalMarkNode } from "@lexical/mark";
import { $applyNodeReplacement } from "lexical";
import type {
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
  SerializedElementNode,
  Spread,
} from "lexical";

export type SerializedProofreadingMarkNode = Spread<
  {
    ids: Array<string>;
  },
  SerializedElementNode
>;

const NO_IDS: readonly string[] = [];

export class ProofreadingMarkNode extends MarkNode {
  constructor(ids: readonly string[] = NO_IDS, key?: NodeKey) {
    super(ids, key);
  }

  static getType(): string {
    return "proofreading-mark";
  }

  static clone(node: ProofreadingMarkNode): ProofreadingMarkNode {
    return new ProofreadingMarkNode(node.__ids, node.__key);
  }

  static importJSON(serializedNode: SerializedProofreadingMarkNode): ProofreadingMarkNode {
    return $createProofreadingMarkNode(serializedNode.ids).updateFromJSON(serializedNode);
  }

  override updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedProofreadingMarkNode>): this {
    return super.updateFromJSON(serializedNode).setIDs(serializedNode.ids);
  }

  override exportJSON(): SerializedProofreadingMarkNode {
    return {
      ...super.exportJSON(),
      ids: this.getIDs(),
    };
  }

  override insertNewAfter(
    _selection: Parameters<LexicalMarkNode["insertNewAfter"]>[0],
    restoreSelection = true,
  ) {
    const markNode = $createProofreadingMarkNode(this.__ids);
    this.insertAfter(markNode, restoreSelection);
    return markNode;
  }
}

export function $createProofreadingMarkNode(
  ids: readonly string[] = NO_IDS,
): ProofreadingMarkNode {
  return $applyNodeReplacement(new ProofreadingMarkNode(ids));
}

export function $isProofreadingMarkNode(node: LexicalNode | null): node is ProofreadingMarkNode {
  return node instanceof ProofreadingMarkNode;
}
